import { Link, NavLink, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ChevronRight, FileAudio, Folder, ListMusic, Play, Shuffle } from 'lucide-react';
import { useNavItems } from '../components/Shell';
import { useState } from 'react';
import type { AlbumSort, Track, TrackSort } from '@pujolisique/shared';
import { useAlbums, useArtists, useComposers, useFolder, useGenres, useTrackPages } from '../api/hooks';
import { api } from '../api/client';
import { toast } from '../ui/toast';
import { AlbumCard, Grid, PersonCard } from '../components/Cards';
import { Empty, ErrorBox, LoadMore, Loading } from '../components/States';
import { TrackList } from '../components/TrackList';
import { DeleteButton } from '../components/trackActions';
import { usePlayer } from '../player/store';
import { hashHue } from '../lib/format';

const SORTS: { id: AlbumSort; label: string }[] = [
  { id: 'name', label: 'Titre' },
  { id: 'newest', label: 'Ajoutés récemment' },
  { id: 'year', label: 'Année de sortie' },
  { id: 'artist', label: 'Artiste' },
  { id: 'frequent', label: 'Les plus écoutés' },
  { id: 'recent', label: 'Écoutés récemment' },
  { id: 'random', label: 'Au hasard' },
];

export function AlbumsPage() {
  const [params, setParams] = useSearchParams();
  const sort = (SORTS.find((s) => s.id === params.get('sort'))?.id ?? 'name') as AlbumSort;
  const q = useAlbums(sort);
  const items = q.data?.pages.flatMap((p) => p.items) ?? [];
  return (
    <div className="page">
      <div className="page__bar">
        <h1 className="page__title">Albums</h1>
        <label className="select">
          <span className="sr-only">Trier par</span>
          <select value={sort} onChange={(e) => setParams({ sort: e.target.value }, { replace: true })}>
            {SORTS.map((s) => (
              <option key={s.id} value={s.id}>
                {s.label}
              </option>
            ))}
          </select>
        </label>
      </div>
      {q.isLoading && <Loading />}
      {q.error && <ErrorBox error={q.error} retry={() => q.refetch()} />}
      {q.data && !items.length && <Empty title="Aucun album ici">Ce tri n’a encore rien à montrer.</Empty>}
      <Grid>
        {items.map((a) => (
          <AlbumCard key={a.id} album={a} />
        ))}
      </Grid>
      <LoadMore active={!!q.hasNextPage} onMore={() => !q.isFetchingNextPage && q.fetchNextPage()} />
    </div>
  );
}

const TRACK_SORTS: { id: TrackSort; label: string }[] = [
  { id: 'name', label: 'Titre (A → Z)' },
  { id: 'artist', label: 'Artiste' },
  { id: 'album', label: 'Album' },
  { id: 'year', label: 'Année' },
  { id: 'newest', label: 'Ajoutés récemment' },
  { id: 'frequent', label: 'Les plus écoutés' },
  { id: 'recent', label: 'Écoutés récemment' },
];

/** Tous les titres de musique, triables, avec lecture de toute la liste ou au hasard */
export function TracksPage() {
  const [params, setParams] = useSearchParams();
  const sort = (TRACK_SORTS.find((s) => s.id === params.get('sort'))?.id ?? 'name') as TrackSort;
  const q = useTrackPages(sort);
  const items = q.data?.pages.flatMap((p) => p.items) ?? [];
  const total = q.data?.pages[0]?.total;
  const playTracks = usePlayer((s) => s.playTracks);
  const [busy, setBusy] = useState(false);

  // Lecture de la bibliothèque entière (jusqu'à 500 titres), dans l'ordre affiché ou au hasard
  const playAll = async (shuffle: boolean) => {
    setBusy(true);
    try {
      const all =
        shuffle || q.hasNextPage ? (await api<{ items: Track[] }>(`/api/tracks?sort=${shuffle ? 'random' : sort}&limit=500`)).items : items;
      if (all.length) playTracks(all, 0, { shuffle, source: 'Titres' });
    } catch (e) {
      toast((e as Error).message, 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="page">
      <div className="page__bar">
        <h1 className="page__title">
          Titres{total != null && <span className="page__count">{total.toLocaleString('fr-FR')}</span>}
        </h1>
        <label className="select">
          <span className="sr-only">Trier par</span>
          <select value={sort} onChange={(e) => setParams({ sort: e.target.value }, { replace: true })}>
            {TRACK_SORTS.map((s) => (
              <option key={s.id} value={s.id}>
                {s.label}
              </option>
            ))}
          </select>
        </label>
      </div>
      {items.length > 0 && (
        <div className="row-gap tracks-actions">
          <button className="btn btn--gold" onClick={() => playAll(false)} disabled={busy}>
            <Play size={16} fill="currentColor" /> Tout lire
          </button>
          <button className="btn" onClick={() => playAll(true)} disabled={busy}>
            <Shuffle size={16} /> Aléatoire
          </button>
        </div>
      )}
      {q.isLoading && <Loading />}
      {q.error && <ErrorBox error={q.error} retry={() => q.refetch()} />}
      {q.data && !items.length && (
        <Empty title={sort === 'frequent' || sort === 'recent' ? 'Rien écouté pour l’instant' : 'Aucun titre'}>
          {sort === 'frequent' || sort === 'recent' ? 'Ce tri se remplit au fil de vos écoutes.' : 'Ajoutez de la musique dans le dossier de la bibliothèque.'}
        </Empty>
      )}
      <TrackList tracks={items} source="Titres" showCover showAlbum />
      <LoadMore active={!!q.hasNextPage} onMore={() => !q.isFetchingNextPage && q.fetchNextPage()} />
    </div>
  );
}

export function ArtistsPage() {
  const q = useArtists();
  const items = q.data?.pages.flatMap((p) => p.items) ?? [];
  return (
    <div className="page">
      <h1 className="page__title">Artistes</h1>
      {q.isLoading && <Loading />}
      {q.error && <ErrorBox error={q.error} retry={() => q.refetch()} />}
      <Grid>
        {items.map((a) => (
          <PersonCard key={a.id} person={a} kind="artist" />
        ))}
      </Grid>
      <LoadMore active={!!q.hasNextPage} onMore={() => !q.isFetchingNextPage && q.fetchNextPage()} />
    </div>
  );
}

export function ComposersPage() {
  const q = useComposers();
  const items = q.data?.pages.flatMap((p) => p.items) ?? [];
  return (
    <div className="page">
      <h1 className="page__title">Compositeurs</h1>
      <p className="page__lead">Toutes les œuvres de la bibliothèque, regroupées par la personne qui les a écrites, quel que soit l’interprète.</p>
      {q.isLoading && <Loading />}
      {q.error && <ErrorBox error={q.error} retry={() => q.refetch()} />}
      {q.data && !items.length && (
        <Empty title="Aucun compositeur trouvé">Renseignez la balise « Compositeur » (COMPOSER) de vos fichiers puis relancez l’analyse de la bibliothèque.</Empty>
      )}
      <Grid>
        {items.map((c) => (
          <PersonCard key={c.id} person={c} kind="composer" />
        ))}
      </Grid>
      <LoadMore active={!!q.hasNextPage} onMore={() => !q.isFetchingNextPage && q.fetchNextPage()} />
    </div>
  );
}

export function GenresPage() {
  const q = useGenres();
  return (
    <div className="page">
      <h1 className="page__title">Genres</h1>
      {q.isLoading && <Loading />}
      {q.error && <ErrorBox error={q.error} retry={() => q.refetch()} />}
      <div className="genres">
        {q.data?.map((g) => (
          <Link key={g.id} to={`/genre/${encodeURIComponent(g.id)}`} className="genre" style={{ ['--h' as string]: hashHue(g.name) }}>
            <span className="genre__name">{g.name}</span>
            {g.trackCount !== undefined && <span className="genre__count">{g.trackCount} titres</span>}
          </Link>
        ))}
      </div>
    </div>
  );
}

export function FoldersPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const q = useFolder(id);
  const playTracks = usePlayer((s) => s.playTracks);
  const d = q.data;
  const files = d?.entries.filter((e) => e.kind === 'file' && e.track).map((e) => e.track!) ?? [];
  return (
    <div className="page">
      <nav className="crumbs" aria-label="Fil d’Ariane">
        <Link to="/folders">Bibliothèque</Link>
        {d?.parents.map((p) => (
          <span key={p.id}>
            <ChevronRight size={14} aria-hidden />
            <Link to={`/folders/${p.id}`}>{p.name}</Link>
          </span>
        ))}
        {d && id && (
          <span>
            <ChevronRight size={14} aria-hidden />
            <span aria-current="page">{d.name}</span>
          </span>
        )}
      </nav>
      <div className="page__bar">
        <h1 className="page__title">{id ? (d?.name ?? '…') : 'Dossiers'}</h1>
        <div className="row-gap">
          {files.length > 0 && (
            <button className="btn btn--gold" onClick={() => playTracks(files, 0, { source: d?.name })}>
              <Play size={16} fill="currentColor" /> Lire le dossier
            </button>
          )}
          {id && d && d.parents.length > 0 && d.entries.length > 0 && (
            <DeleteButton
              scope={{ folder: id }}
              label={`le dossier « ${d.name} »`}
              text="Supprimer le dossier"
              onDone={() => nav(`/folders/${d.parents[d.parents.length - 1].id}`, { replace: true })}
            />
          )}
        </div>
      </div>
      {q.isLoading && <Loading />}
      {q.error && <ErrorBox error={q.error} retry={() => q.refetch()} />}
      <ul className="folders">
        {d?.entries
          .filter((e) => e.kind === 'folder')
          .map((e) => (
            <li key={e.id}>
              <Link to={`/folders/${e.id}`} className="folder">
                <Folder size={18} aria-hidden />
                <span>{e.name}</span>
                <ChevronRight size={16} aria-hidden className="folder__chev" />
              </Link>
            </li>
          ))}
      </ul>
      {files.length > 0 && (
        <section className="section">
          <h2 className="section__title section__title--icon">
            <FileAudio size={18} aria-hidden /> Fichiers
          </h2>
          <TrackList tracks={files} source={d?.name} showCover />
        </section>
      )}
      {d && !d.entries.length && <Empty title="Dossier vide" />}
    </div>
  );
}

/** Hub « Bibliothèque » pour mobile */
export function LibraryPage() {
  const items = useNavItems();
  const links = [...items.slice(0, 5), { to: '/playlists', label: 'Playlists', icon: ListMusic }, ...items.slice(5)];
  return (
    <div className="page">
      <h1 className="page__title">Bibliothèque</h1>
      <ul className="hub">
        {links.map((l) => (
          <li key={l.to}>
            <NavLink to={l.to} className="hub__link">
              <l.icon size={22} aria-hidden />
              <span>{l.label}</span>
              <ChevronRight size={18} aria-hidden className="hub__chev" />
            </NavLink>
          </li>
        ))}
      </ul>
    </div>
  );
}
